import { Button } from '@/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/dropdown';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { DataTable } from '@/components/table/datatable';
import { clearErrors } from '@/reducers/events';
import { AppDispatch } from '@/root/store';
import {
  DeleteCommittees,
  GetCommittees,
  StoreCommittee,
  UpdateCommittee,
} from '@/services/events';
import {
  NoSymbolIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  DocumentCheckIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/solid';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

interface Committee {
  id: number;
  name: string;
  email: string;
  role: string;
}

export function OrgEventCommittee() {
  const [page, setPage] = useState(1);
  const [selectedCommittee, setSelectedCommittee] = useState<any>({});
  const [showDelete, setShowDelete] = useState({
    modal: false,
    id: 0,
  });
  const [showModal, setShowModal] = useState({
    mode: 'create',
    status: false,
  });
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const { slug } = useParams();

  const [formData, setFormData] = useState({ email: '', role: '' });

  const setData = (value: string, element: string) => {
    setFormData({ ...formData, [element]: value });

    dispatch(clearErrors());
  };

  const { event, committees, committee, paginator, errors } = useSelector(
    (state: any) => state.event
  );

  const ROLES = [
    { id: 'co-owner', name: 'Co-Owner' },
    { id: 'ticketing', name: 'Ticketing' },
    { id: 'scanner', name: 'Scanner' },
    { id: 'finance', name: 'Finance' },
  ];

  const submit = () => {
    if (showModal.mode === 'create') {
      dispatch(StoreCommittee(event.id, formData));
    } else if (showModal.mode === 'edit') {
      dispatch(UpdateCommittee(event.id, selectedCommittee?.id, formData));
    }

    setFormData({ email: '', role: '' });
  };

  const queryParams = new URLSearchParams(location.search);
  const searchQuery: string = queryParams.get('search') ?? '';

  useEffect(() => {
    setShowModal({ ...showModal, status: false });
    setShowDelete({ modal: false, id: 0 });

    dispatch(GetCommittees(slug, searchQuery, page));
  }, [searchQuery, page, committee]);

  const columns: ColumnDef<Committee>[] = [
    {
      header: 'Panitia',
      cell: ({ row }) => {
        const name = row.original.name;
        const email = row.original.email;
        return (
          <div>
            <p>{name}</p>
            <span className='text-gray-500'>{email}</span>
            <div></div>
          </div>
        );
      },
    },
    {
      header: 'Role',
      accessorKey: 'role',
    },
    {
      header: 'Aksi',
      cell: ({ row }) => {
        return (
          <>
            {row.original.role !== 'owner' &&
              ['superadmin', 'owner'].includes(
                event?.logged_in_organizer?.role
              ) && (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <Button
                      size='sm'
                      variant='neutral'
                      leftIcon={<EllipsisHorizontalIcon />}
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() => {
                        setSelectedCommittee(row.original);
                        setFormData({
                          email: row.original.email,
                          role: row.original.role,
                        });
                        setShowModal({ mode: 'edit', status: true });
                      }}
                    >
                      <PencilSquareIcon className='mr-2 h-4 w-4' />
                      <span>Ubah</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        setShowDelete({ modal: true, id: row.original.id })
                      }
                    >
                      <TrashIcon className='mr-2 h-4 w-4 text-danger' />
                      <span className='text-danger'>Hapus</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
          </>
        );
      },
    },
  ];

  const ctaButton = (
    <Button
      onClick={() => {
        dispatch(clearErrors());
        setFormData({ email: '', role: '' });
        setShowModal({ mode: 'create', status: true });
      }}
      leftIcon={<PlusIcon />}
    >
      Tambah panitia
    </Button>
  );

  return (
    <>
      <Modal
        show={showModal.status}
        onRequestClose={() => setShowModal({ ...showModal, status: false })}
        width='medium'
        title={`${showModal.mode === 'create' ? 'Tambah' : 'Ubah'} panitia`}
        body={
          <div className='flex flex-col gap-4'>
            <div className='mb-3'>
              <FormLabel
                label='Email'
                errorText={errors.email ?? errors.organizer_id}
                required
              >
                <Input
                  type='email'
                  value={formData.email}
                  disabled={showModal.mode === 'edit'}
                  onChange={(e) => setData(e.target.value, 'email')}
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel label='Pilih role' errorText={errors.role} required>
                <Select
                  onValueChange={(e) => {
                    setData(e, 'role');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue defaultValue={'ticketing'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {ROLES.map(
                        (item: { id: string; name: string }, key: number) => (
                          <SelectItem key={key} value={item.id}>
                            {item.name}
                          </SelectItem>
                        )
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormLabel>
            </div>
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setShowModal({ ...showModal, status: false })}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button onClick={submit} leftIcon={<DocumentCheckIcon />}>
              Simpan
            </Button>
          </div>
        }
      />

      <Modal
        show={showDelete.modal}
        onRequestClose={() => setShowDelete({ ...showDelete, modal: false })}
        width='medium'
        title='Hapus Panitia'
        body={
          <>
            <p>Apakah kamu yakin ingin menghapus panitia ini?</p>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setShowDelete({ ...showDelete, modal: false })}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              variant={'danger'}
              onClick={() => {
                dispatch(DeleteCommittees(event.id, showDelete.id));
              }}
              leftIcon={<TrashIcon />}
            >
              Ya, Hapus
            </Button>
          </div>
        }
      />

      <DataTable
        name='Daftar panitia'
        noun={'panitia'}
        ctaButton={
          ['superadmin', 'owner'].includes(event?.logged_in_organizer?.role) ? (
            ctaButton
          ) : (
            <></>
          )
        }
        columns={columns}
        data={committees}
        total={paginator.to}
        currentTotal={paginator.total}
        currentPage={paginator.current_page}
        pages={paginator.last_page}
        onPageRequest={(nextPage) => setPage(nextPage)}
      />
    </>
  );
}
